import type React from 'react';
import { observable, action, configure, makeObservable } from 'mobx';
import { IEdetector } from '@utils/browserUTLs';
import {
  setMobileFiltersBodyState,
  setMobileMenuBodyState
} from './modalsBodyEffects';

configure({
  enforceActions: 'observed',
  useProxies: IEdetector() ? 'never' : 'always'
});

export const consentModalKey = 'isConsentModalAgreed';

export interface LeaveFeedbackParams {
  orderId?: number;
  isOpenTipTab?: boolean;
  isRateRecipe?: boolean;
  recipeId?: string;
  modalTitle?: string;
  getRecipeVotedRating?: (value: number) => void;
  onSuccessfulReviewSubmit?: () => void;
}

// ЭТАП 1 · Шаг 3 — SSR-safe: localStorage на module-eval, на сервере его нет.
const isConsentModalAgreed =
  typeof localStorage !== 'undefined' &&
  localStorage.getItem(consentModalKey) === 'true';

class Store {
  showLoginModal = false;

  isConsentModalAgreed: boolean = isConsentModalAgreed;

  isConsentModalOpened: boolean = false;

  showCompleteModal = false;

  finishReg = false;

  showMergeModal = false;

  isRegistrationFlag = false;

  sourceCartToken = '';

  targetCartToken = '';

  afterMergeFunc = (): void => {};

  mobileCatalogFilters = false;

  mobileMainMenu = false;

  isShowQRCode: string | null = null;

  isShowClientQrModal = false;

  loginErrorMsg = '';

  isShowCallbackModal = false;

  showLeftSiteModal = false;

  isShowEditOrder20Modal: string | null = null;

  isShowDigitalModal = 0;
  // isShowDigitalModal = 70224;

  isShowShareSocials = false;

  shareSocials: {
    onListClick?: () => void;
    isText?: string;
    link?: string;
    shareLink?: () => void;
  } = {};

  quickInfoProductId: string | number | null = null;

  quickInfoCompareMouse?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    pathname: string
  ) => void = undefined;

  videoModal: {
    url: string;
    isDesktop?: boolean;
    isTablet?: boolean;
  } | null = null;

  leaveFeedback: LeaveFeedbackParams | null = null;

  constructor() {
    makeObservable(this, {
      showLoginModal: observable,
      showCompleteModal: observable,
      showMergeModal: observable,
      isRegistrationFlag: observable,
      sourceCartToken: observable,
      targetCartToken: observable,
      afterMergeFunc: observable,
      finishReg: observable,
      mobileMainMenu: observable,
      mobileCatalogFilters: observable,
      isShowQRCode: observable,
      isShowClientQrModal: observable,
      isShowEditOrder20Modal: observable,
      isShowDigitalModal: observable,
      isShowShareSocials: observable,
      shareSocials: observable,
      quickInfoProductId: observable,
      quickInfoCompareMouse: observable,
      videoModal: observable,
      leaveFeedback: observable,
      isConsentModalOpened: observable,
      isConsentModalAgreed: observable,
      loginErrorMsg: observable,
      isShowCallbackModal: observable,
      showLeftSiteModal: observable,
      setIsConsentModalOpened: action,
      setIsConsentModalAgreed: action,
      openCallbackModal: action,
      closeCallbackModal: action,
      changeAuthModalState: action,
      changeCompleteModalState: action,
      closeMergeModal: action,
      changeAuthToMerge: action,
      showFinishReg: action,
      hideFinishReg: action,
      toggleMobileCatalogFilters: action,
      toggleMobileMainMenu: action,
      changeQRCodeState: action,
      changeLoginErrorMsg: action,
      showClientQrModal: action,
      hideClientQrModal: action,
      closeModalleftSite: action,
      openModalleftSite: action,
      openEditOrder20Modal: action,
      closeEditOrder20Modal: action,
      openDigitalModal: action,
      closeDigitalModal: action,
      openShareSocials: action,
      closeShareSocials: action,
      openQuickInfo: action,
      closeQuickInfo: action,
      openVideoModal: action,
      closeVideoModal: action,
      openLeaveFeedback: action,
      closeLeaveFeedback: action
    });
  }

  openLeaveFeedback = (params: LeaveFeedbackParams = {}): void => {
    this.leaveFeedback = params;
  };

  closeLeaveFeedback = (): void => {
    this.leaveFeedback = null;
  };

  openVideoModal = (params: {
    url: string;
    isDesktop?: boolean;
    isTablet?: boolean;
  }): void => {
    this.videoModal = params;
  };

  closeVideoModal = (): void => {
    this.videoModal = null;
  };

  openQuickInfo = (
    productId: string | number,
    compareMouse?: (
      e: React.MouseEvent<HTMLAnchorElement>,
      pathname: string
    ) => void
  ): void => {
    this.quickInfoProductId = productId;
    this.quickInfoCompareMouse = compareMouse;
  };

  closeQuickInfo = (): void => {
    this.quickInfoProductId = null;
    this.quickInfoCompareMouse = undefined;
  };

  setIsConsentModalAgreed = (value: boolean): void => {
    this.isConsentModalAgreed = value;
    localStorage.setItem(consentModalKey, String(value));
  };

  setIsConsentModalOpened = (value: boolean): void => {
    this.isConsentModalOpened = value;
  };

  openShareSocials = (args: {
    onListClick: () => void;
    isText: string;
    link: string;
    shareLink: () => void;
  }): void => {
    const { isText, link, shareLink, onListClick } = args;

    this.isShowShareSocials = true;
    this.shareSocials = {
      isText,
      link,
      shareLink,
      onListClick
    };
  };

  closeShareSocials = (): void => {
    this.isShowShareSocials = false;
    this.shareSocials = {};
  };

  openDigitalModal = (product): void => {
    this.isShowDigitalModal = product;
  };

  closeDigitalModal = (): void => {
    this.isShowDigitalModal = 0;
  };

  openEditOrder20Modal = (order: string | null): void => {
    this.isShowEditOrder20Modal = order;
  };

  closeEditOrder20Modal = (): void => {
    this.isShowEditOrder20Modal = null;
  };

  closeCallbackModal = (): void => {
    this.isShowCallbackModal = false;
  };

  openCallbackModal = (): void => {
    this.isShowCallbackModal = true;
  };

  closeModalleftSite = (): void => {
    this.showLeftSiteModal = false;
  };

  openModalleftSite = (): void => {
    this.showLeftSiteModal = true;
  };

  showFinishReg = (): void => {
    this.finishReg = true;
  };

  hideFinishReg = (): void => {
    this.finishReg = false;
    location.hash = '';
  };

  changeLoginErrorMsg = (msg: string): void => {
    this.loginErrorMsg = msg;
  };

  changeAuthModalState = (): void => {
    this.showLoginModal = !this.showLoginModal;
  };

  changeCompleteModalState = (): void => {
    this.showCompleteModal = !this.showCompleteModal;
  };

  closeMergeModal = (): void => {
    this.showMergeModal = false;
    this.isRegistrationFlag = false;
  };

  changeAuthToMerge = (
    tokens: {
      targetCartToken: string;
      sourceCartToken: string;
      registration: boolean;
    },
    func: () => void
  ): void => {
    const { targetCartToken, sourceCartToken, registration } = tokens;

    this.targetCartToken = targetCartToken;
    this.sourceCartToken = sourceCartToken;
    this.afterMergeFunc = func;

    this.showMergeModal = true;
    this.isRegistrationFlag = registration;
    this.showLoginModal = false;
  };

  toggleMobileCatalogFilters = (): void => {
    this.mobileCatalogFilters = !this.mobileCatalogFilters;
    setMobileFiltersBodyState(this.mobileCatalogFilters);
  };

  toggleMobileMainMenu = (): void => {
    this.mobileMainMenu = !this.mobileMainMenu;
    if (this.mobileCatalogFilters) {
      this.toggleMobileCatalogFilters();
    }
    setMobileMenuBodyState(this.mobileMainMenu);
  };

  changeQRCodeState = (link: string | null): void => {
    this.isShowQRCode = link;
  };

  showClientQrModal = (): void => {
    this.isShowClientQrModal = true;
  };

  hideClientQrModal = (): void => {
    this.isShowClientQrModal = false;
  };
}

const modalsStore = new Store();
export type ModalsStoreType = typeof modalsStore;
export default modalsStore;
